import axios, { AxiosResponse } from "axios";
import { TableData } from "../../models/dtos/base/TableData";
import TitleValue from "../../models/dtos/base/TitleValue";
import { ExamAnswerJson } from "../../models/dtos/exams/exam/ExamAnswerJson";
import { ExamRequestDTO } from "../../models/dtos/exams/exam/ExamRequestDTO";
import { ExamRequestedDTO } from "../../models/dtos/exams/exam/ExamRequestedDTO";
import { ExamResultEditionDTO } from "../../models/dtos/exams/exam/ExamResultEditionDTO";
import { ExamResultsDTO } from "../../models/dtos/exams/exam/ExamResultsDTO";
import { ExamQuestionsView } from "../../models/dtos/exams/questions/ExamQuestionsView";
import { QueryPagesDTO } from "../../models/query-strings/base/QueryPagesDTO";
import { ExamEntranceQueryString } from "../../models/query-strings/exams/exam/ExamEntranceQueryString";
import { ExamResultQueryString } from "../../models/query-strings/exams/exam/ExamResultQueryString";
import BaseService from "../base/BaseService";
import { QuestionOptions } from "../../models/interfaces/exams/questions/QuestionOptions";

export interface ExamStatisticsDTO {
  id: number;
  title: string;
  examDate: string | null;
  baseIds: number[] | null;
  baseTitles: string;
  fieldId: number | null;
  fieldTitle: string;
  participantsCount: number;
  examPercent: number;
  examLevel: number;
  examEnded: boolean;
  examWrongAnswer: number;
  examCorrectAnswer: number;
  examUnAnswer: number;
  totalQuestion: number;
  studentExamRank: number;
  courseStatistics: CourseStatisticView[];
  alreadyJoined: boolean;
}

export interface CourseStatisticView {
  courseId: number;
  courseTitle: string;
  count: number;
  correctCount: number;
  wrongCount: number;
  unAnswered: number;
  topPercent: number;
  avragePrecent: number;
  coefficient: number;
  percent: number;
  level: number;
  rank: number;
}

export interface ExamLevelDTO {
  chartData: {
    title: string;
    key: string;
    data: { title: string; value: number }[];
  }[];
}

export default class ExamResultService extends BaseService {
  getExamsRequested(
    queryString: QueryPagesDTO
  ): Promise<TableData<ExamRequestedDTO>> {
    return axios
      .get("/StudentExam/Student/GetExamsRequested", { params: queryString })
      .then(function (
        response: AxiosResponse<TableData<ExamRequestedDTO>, any>
      ) {
        return response.data;
      });
  }

  getExamLevel(examId: string, examResultId: string): Promise<ExamLevelDTO> {
    return axios
      .get(`/ExamResult/ExamLevelChart/${examId}/${examResultId}`)
      .then(function (response: AxiosResponse<ExamLevelDTO, any>) {
        return response.data;
      });
  }

  getExamResultReport(
    examId: number,
    studentExamResultId: number
  ): Promise<StudentExamResultReportDTO[]> {
    return axios
      .get(`/ExamResult/Report/${examId}/${studentExamResultId}`)
      .then(function (
        response: AxiosResponse<StudentExamResultReportDTO[], any>
      ) {
        return response.data;
      });
  }

  removeExamRequest(examRequestId: number): Promise<any> {
    return axios
      .delete(`/StudentExam/RemoveExamRequest/${examRequestId}`)
      .then(function (response) {
        return response.status;
      });
  }

  getExamEntrance(
    queryString: ExamEntranceQueryString
  ): Promise<TitleValue<number>[]> {
    return axios
      .get("/StudentExam/GetExamEntrance", { params: queryString })
      .then(function (response: AxiosResponse<TitleValue<number>[], any>) {
        return response.data;
      });
  }

  examRequest(dto: ExamRequestDTO): Promise<any> {
    return axios
      .post("/StudentExam/ExamRequest", dto)
      .then(function (response: AxiosResponse<any, any>) {
        return response.status;
      });
  }

  getDateTime(examId: number): Promise<number> {
    // return axios.get(`/ExamResult/ExamEndTime/${examId}`)
    //     .then(function (response: AxiosResponse<Date, any>) {
    //         return response.data;
    //     });
    return axios
      .get(`/ExamResult/ExamDuration/${examId}`)
      .then(function (response: AxiosResponse<number, any>) {
        return response.data;
      });
  }

  startExam(dto: ExamResultEditionDTO): Promise<any> {
    return axios
      .post(`/ExamResult`, dto)
      .then(function (response: AxiosResponse<any, any>) {
        return response;
      });
  }

  updateExam(dto: ExamResultEditionDTO): Promise<any> {
    return axios
      .put(`/ExamResult`, dto)
      .then(function (response: AxiosResponse<any, any>) {
        return response;
      });
  }

  endExam(dto: ExamResultEditionDTO): Promise<any> {
    return axios
      .put(`/ExamResult/EndExam`, dto)
      .then(function (response: AxiosResponse<any, any>) {
        return response;
      });
  }

  getExamResult(examResultId: number): Promise<ExamResultsDTO> {
    return axios
      .get(`/ExamResult/StudentExam/${examResultId}`)
      .then(function (response: AxiosResponse<ExamResultsDTO, any>) {
        return response.data;
      });
  }

  recentlyExams(): Promise<ExamResultsDTO[]> {
    return axios
      .get(`/ExamResult/RecentlyExams`)
      .then(function (response: AxiosResponse<ExamResultsDTO[], any>) {
        return response.data;
      });
  }

  getPage(queryString: ExamResultQueryString): Promise<ExamResultsDTO[]> {
    return axios
      .get(`/ExamResult/StudentExams`, { params: queryString })
      .then(function (response: AxiosResponse<ExamResultsDTO[], any>) {
        return response.data;
      });
  }

  getExamStatistics(
    examId: string,
    examResultId: string
  ): Promise<ExamStatisticsDTO> {
    return axios
      .get(`/ExamResult/ExamStatistics/${examId}/${examResultId}`)
      .then(function (response: AxiosResponse<ExamStatisticsDTO, any>) {
        return response.data;
      });
  }

  getExamStructure(examResultId: number): Promise<{
    courseIds: number[];
    structure: { courseIds: number[]; groupTitle: string }[];
  }> {
    return axios
      .get(`/ExamResult/ExamStructure/${examResultId}`)
      .then(function (
        response: AxiosResponse<
          {
            courseIds: number[];
            structure: { courseIds: number[]; groupTitle: string }[];
          },
          any
        >
      ) {
        return response.data;
      });
  }

  getAnswerSheet(examResultId: number): Promise<ExamQuestionsView[]> {
    return axios
      .get(`/ExamResult/AnswerSheet/${examResultId}`)
      .then(function (response: AxiosResponse<ExamAnswerJson[], any>) {
        let result: ExamQuestionsView[] = [];

        if (response.status === 200) {
          result = response.data.map((question, key) => ({
            answers: question.questions,
            orderId: key + 1,
            parentId: 0,
            relatedQuestions: [],
            FullAttachment: question.fullAttachment,
            index: key + 1,
            studentSelectedAnswerOption: question.studentSelectedOption,
            ...question,
          }));
        }

        return result;
      });
  }
}

export interface StudentExamResultReportDTO {
  studentExamResultID: number;
  questionStoreID: number;
  studetnSelectedAnswer: QuestionOptions;
  studentUserID: string;
}
